import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { requestJSON } from "../api";
import { useI18n } from "../i18n";
import { ManagementLoading, ManagementMessages, ManagementPanel } from "../managementUI";

type Collection = {
  source: string;
  status: "unobserved" | "idle" | "running" | "failed";
  last_success_ms: number | null;
  last_attempt_ms: number | null;
  collected_until_ms: number | null;
};
export type BillingDiagnostics = {
  runtime_enabled: boolean;
  token_worker_running: boolean;
  worker_scope: "responding_api_process";
  pricing_revision: number;
  points_per_usd: string;
  category_modes: Record<string, string>;
  token_collection: Collection;
  token_reconciliation?: Collection | null;
};
const categories = [
  ["token", "Token", "Tokens"], ["sandbox", "沙箱", "Sandboxes"],
  ["storage", "存储", "Storage"], ["database", "数据库", "Databases"],
  ["publish", "发布环境", "Published environments"],
] as const;
const modes: Record<string, [string,string]> = {
  disabled: ["未启用计价", "Pricing disabled"], free: ["免费", "Free"],
  unit_price: ["按用量单价", "Usage unit price"], cost_factor: ["费用乘倍率", "Cost multiplier"],
  resource_price: ["按资源单价", "Resource unit prices"],
};
function CollectionCard({ data, history = false }: { data?: Collection | null; history?: boolean }) {
  const { t } = useI18n();
  const time = (value?: number | null) => value == null ? t("尚无观测", "Not observed") : new Date(value).toLocaleString(t("zh-CN", "en-US"));
  const labels: Record<Collection["status"], [string,string]> = {
    unobserved: ["尚无观测", "Not observed"], idle: ["等待下一轮采集", "Waiting for next collection"],
    running: ["采集中", "Collecting"], failed: ["最近一次采集失败", "Latest collection failed"],
  };
  return <section className="billing-card">
    <h3>{history ? t("Token 历史补采", "Token historical replay") : t("Token 持续采集", "Token collection")}</h3>
    {!data ? <p>{t("当前服务未提供历史补采状态，不能判断是否完成。", "The service does not report historical replay status; completion is unknown.")}</p> : <>
      <p role={data.status === "failed" ? "alert" : undefined}><strong>{t(...labels[data.status])}</strong></p>
      <dl className="billing-diagnostic-times">
        <dt>{t("最近尝试", "Last attempt")}</dt><dd>{time(data.last_attempt_ms)}</dd>
        <dt>{t("最近成功", "Last success")}</dt><dd>{time(data.last_success_ms)}</dd>
        <dt>{history ? t("本轮补采进度", "Current replay cursor") : t("已处理游标", "Processed cursor")}</dt><dd>{time(data.collected_until_ms)}</dd>
      </dl>
    </>}
    <p className="monitor-note">{history
      ? t("滚动回查最近七天，完成一轮后游标会回到窗口起点。游标不是全部费用已结算的证明。", "Replays the last seven days. The cursor resets after each sweep and does not prove all costs are settled.")
      : t("采集进度不等于供应商账单，也不保证迟到记录全部到齐。没有观测记录不代表零消耗。", "Collection progress is not a supplier invoice and does not guarantee all late records arrived. No observations does not mean zero usage.")}</p>
  </section>;
}
export function BillingDiagnosticsView({ data }: { data: BillingDiagnostics }) {
  const { t } = useI18n();
  return <>
    <dl className="monitor-summary">
      <div><dt>{t("计费运行", "Billing runtime")}</dt><dd>{data.runtime_enabled ? t("已启用", "Enabled") : t("未启用", "Disabled")}</dd></div>
      <div><dt>{t("当前服务的 Token 采集任务", "This service's Token worker")}</dt><dd>{data.token_worker_running ? t("运行中", "Running") : t("未运行", "Not running")}</dd></div>
      <div><dt>{t("计价规则版本", "Pricing revision")}</dt><dd>{data.pricing_revision}</dd></div>
      <div><dt>{t("每美元对应点数", "Points per USD")}</dt><dd>{data.points_per_usd}</dd></div>
    </dl>
    <p className="monitor-note">{t("任务状态仅反映本次响应的 API 服务进程；持久化采集游标由服务副本共享，单个进程未运行不能证明整个集群已停止采集。", "Worker state describes only the API process serving this response. Persisted cursors are shared; an inactive process does not prove collection stopped across the cluster.")}</p>
    <div className="billing-diagnostic-cards"><CollectionCard data={data.token_collection} /><CollectionCard data={data.token_reconciliation} history /></div>
    <h3>{t("分类计价状态", "Category pricing")}</h3>
    <div className="administrator-table-wrap"><table className="administrator-table"><thead><tr><th>{t("分类", "Category")}</th><th>{t("当前模式", "Current mode")}</th></tr></thead><tbody>{categories.map(([key,zh,en]) => <tr key={key}><td>{t(zh,en)}</td><td>{t(...(modes[data.category_modes[key]] ?? ["未知", "Unknown"]))}</td></tr>)}</tbody></table></div>
    <p>{t("未启用计价不等于免费。规则配置不会让缺失的用量自动变为可结算数据。", "Disabled pricing is not free pricing. Rules cannot turn missing usage into settlement evidence.")}</p>
    <p><Link to="/billing/records">{t("查看费用与流水", "View costs and ledger")}</Link>{" · "}<Link to="/billing/rules">{t("查看计价规则", "View pricing rules")}</Link>{" · "}<Link to="/billing/prices">{t("查看参考价格同步", "View reference price synchronization")}</Link></p>
  </>;
}
export function BillingDiagnosticsPage() {
  const { t } = useI18n();
  const [data, setData] = useState<BillingDiagnostics | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [loadedAt, setLoadedAt] = useState<number | null>(null);
  useEffect(() => {
    let live = true;
    const controller = new AbortController();
    setBusy(true); setError(null);
    requestJSON<BillingDiagnostics>("/api/v1/billing/diagnostics", { signal: controller.signal })
      .then(value => { if (live) { setData(value); setLoadedAt(Date.now()); } })
      .catch(cause => { if (live) setError(cause); })
      .finally(() => { if (live) setBusy(false); });
    return () => { live = false; controller.abort(); };
  }, [refresh]);
  return <ManagementPanel title={t("计费采集诊断", "Billing collection diagnostics")} eyebrow="COLLECTION" actions={<button type="button" className="secondary-button" disabled={busy} onClick={() => setRefresh(value => value + 1)}>{busy ? t("刷新中…", "Refreshing…") : t("刷新", "Refresh")}</button>}>
    <ManagementMessages error={error} />
    {error && data ? <p role="status">{t("刷新失败，以下保留上次成功查询的结果。", "Refresh failed. The last successful result is shown below.")}</p> : null}
    {busy && !data ? <ManagementLoading /> : null}
    {loadedAt != null ? <p>{t("最近查询时间", "Last retrieved")}: {new Date(loadedAt).toLocaleString(t("zh-CN", "en-US"))}</p> : null}
    {data ? <BillingDiagnosticsView data={data} /> : null}
  </ManagementPanel>;
}
